import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg, todayIST } from "@/lib/api";
import { PageHeader, Field } from "@/components/common";

const card = "rounded-lg border bg-card p-5 space-y-3";

export default function Tally() {
  const { can } = useAuth();
  const T = todayIST();
  const [asOf, setAsOf] = useState(T);
  const [from, setFrom] = useState(`${T.slice(0, 7)}-01`);
  const [to, setTo] = useState(T);
  const [st, setSt] = useState({ godown: "", voucher_type: "" });
  useEffect(() => { api.get("/tally/settings").then((r) => setSt(r.data)); }, []);
  const get = async (path, name) => { try { await download(path, name); toast.success("XML downloaded"); } catch (e) { toast.error(errMsg(e)); } };
  const saveSt = async () => { try { await api.put("/tally/settings", st); toast.success("Tally settings saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <PageHeader title="TallyPrime Export" subtitle="XML files to import into TallyPrime: Gateway of Tally → Import → Masters / Transactions → choose the file." />
      <div className="grid lg:grid-cols-2 gap-4">
        <section className={card} data-testid="tally-masters-card">
          <div className="font-heading font-semibold">1 · Stock Items with opening balance</div>
          <p className="text-sm text-muted-foreground">Creates units, a stock group for each brand and a stock item for each product. The opening quantity and value are taken from drum-wise actual cost on the date you choose. Import this once, before any adjustment vouchers.</p>
          <Field label="Opening balance as of"><Input type="date" max={T} value={asOf} onChange={(e) => setAsOf(e.target.value)} className="sm:w-44" data-testid="tally-asof-input" /></Field>
          <Button onClick={() => get(`/tally/masters.xml?as_of=${asOf}`, `Roxy_Tally_Stock_Items_${asOf}.xml`)} data-testid="tally-masters-btn"><Download className="h-4 w-4 mr-1" />Download Stock Items XML</Button>
        </section>
        <section className={card} data-testid="tally-vouchers-card">
          <div className="font-heading font-semibold">2 · Approved stock adjustments</div>
          <p className="text-sm text-muted-foreground">Each posted adjustment becomes one Stock Journal voucher. Gains go in as destination entries and shortages as source entries, valued at the drum's actual rate.</p>
          <div className="flex flex-wrap gap-3">
            <Field label="From"><Input type="date" max={T} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="tally-from-input" /></Field>
            <Field label="To"><Input type="date" max={T} value={to} onChange={(e) => setTo(e.target.value)} data-testid="tally-to-input" /></Field>
          </div>
          <Button onClick={() => get(`/tally/adjustments.xml?date_from=${from}&date_to=${to}`, `Roxy_Tally_Stock_Journal_${from}_to_${to}.xml`)} data-testid="tally-vouchers-btn"><Download className="h-4 w-4 mr-1" />Download Stock Journal XML</Button>
        </section>
      </div>
      <section className={card} data-testid="tally-settings-card">
        <div className="font-heading font-semibold">Tally company settings</div>
        <p className="text-sm text-muted-foreground">These names must match your TallyPrime company exactly. Stock item names are the product names from Product Master.</p>
        <div className="flex flex-wrap gap-3 items-end">
          <Field label="Godown / location"><Input value={st.godown} disabled={!can("settings:write")} onChange={(e) => setSt({ ...st, godown: e.target.value })} data-testid="tally-godown-input" /></Field>
          <Field label="Voucher type"><Input value={st.voucher_type} disabled={!can("settings:write")} onChange={(e) => setSt({ ...st, voucher_type: e.target.value })} data-testid="tally-vtype-input" /></Field>
          {can("settings:write") && <Button variant="outline" onClick={saveSt} data-testid="tally-settings-save-btn">Save</Button>}
        </div>
      </section>
    </div>
  );
}
